import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'
import { Insight } from '~/packages/general/variable/variables.typing'
import InsightCard from '~/packages/sentinel/insight.component'
import { colors } from '~/design'

interface InsightDetailProps {
  insight: Insight
  x: number
  y: number
  onClose: () => void
  selector?: string
  height?: number | 'auto'
}

const Backdrop = styled.div`
  position: fixed;
  inset: 0 0 58px 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 10;
`

const Container = styled.div<{
  $isMobile: boolean
  $top: number
  $left: number
  $height: number | 'auto'
  $isHidden: boolean
}>`
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  z-index: 12;
  display: flex;
  flex-direction: column;

  ${({ $isMobile, $top, $left, $height, $isHidden }) =>
    $isMobile
      ? `
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 90%;
    max-width: 500px;
    max-height: 80vh;
  `
      : `
    position: absolute;
    top: ${$top}px;
    left: ${$left}px;
    width: 400px;
    height: ${$height === 'auto' ? 'auto' : `${$height}px`};
    max-height: ${$height === 'auto' ? '400px' : 'none'};
    overflow: ${$height === 'auto' ? 'visible' : 'hidden'};
    opacity: ${$isHidden ? 0 : 1};
    transition: opacity 0.2s ease;
  `}
`

const Arrow = styled.div`
  position: absolute;
  right: -12px;
  top: 50%;
  transform: translateY(-50%);
  width: 0;
  height: 0;
  border-top: 12px solid transparent;
  border-bottom: 12px solid transparent;
  border-left: 12px solid white;
`

const CloseButton = styled.button`
  background: ${colors.brandBlueDarker};
  border: none;
  font-size: 24px;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 0;
  right: 0;
  border-radius: 16px;
  color: white;
  transform: translate(50%, -50%);
  z-index: 13;
`

const Content = styled.div`
  padding-bottom: 5px;
  flex: 1;
  overflow: hidden;
  border-radius: 12px;
`

export function InsightDetail({
  insight,
  x,
  y,
  onClose,
  selector = '.wrapper > main',
  height = 'auto',
}: InsightDetailProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [isMobile, setIsMobile] = useState(false)
  const [isContainerHidden, setIsContainerHidden] = useState(true)

  const calculatePosition = (xPos: number, yPos: number) => {
    const tooltipWidth = 400
    const tooltipHeight = height === 'auto' ? containerRef.current?.offsetHeight || 300 : height
    const arrowSize = 12
    const padding = 16

    const parentElement = document.querySelector(selector)
    if (!parentElement) return { top: 0, left: 0 }

    const parentRect = parentElement.getBoundingClientRect()
    const scrollTop = parentElement.scrollTop
    const scrollLeft = parentElement.scrollLeft

    const relativeY = yPos - parentRect.top + scrollTop
    const relativeX = xPos - parentRect.left + scrollLeft + 10

    const top = relativeY - tooltipHeight / 2
    const left = relativeX - tooltipWidth - arrowSize - padding

    return { top, left }
  }

  const [position, setPosition] = useState(() => calculatePosition(x, y))

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768)
    }

    checkMobile()
    window.addEventListener('resize', checkMobile)

    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  useEffect(() => {
    if (!containerRef.current || isMobile) return

    setPosition(calculatePosition(x, y))
  }, [x, y, isMobile, selector])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        onClose()
      }
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleEscape)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [onClose])

  useEffect(() => {
    if (!containerRef.current) return

    if (containerRef.current?.offsetHeight) {
      setIsContainerHidden(false)
    }
  }, [containerRef.current?.offsetHeight])

  const content = (
    <>
      {isMobile && <Backdrop onClick={onClose} />}

      <Container
        ref={containerRef}
        $isMobile={isMobile}
        $top={position.top}
        $left={position.left}
        $height={height}
        $isHidden={isContainerHidden}
      >
        {!isMobile && <Arrow />}

        {isMobile && <CloseButton onClick={onClose}>×</CloseButton>}

        <Content>
          <InsightCard
            key={`insight-popup-${insight.id}`}
            insight={insight}
            data-insight-index={insight.id}
            isExpanded={false}
            isDisabled={false}
            isButton={false}
            isClickable={false}
          />
        </Content>
      </Container>
    </>
  )

  const portalTarget = document.querySelector(selector)
  return portalTarget ? createPortal(content, portalTarget) : null
}
